<script lang="ts">
	import { onMount } from 'svelte';
	import { tagManager, type TagInfo } from '$lib/docs/tags';
	import type { Language } from '$lib/docs/types';
	import { _ } from 'svelte-i18n';
	
	interface Props {
		locale?: Language;
		limit?: number;
		onTagClick?: (tag: string) => void;
	}
	
	let { locale = 'en', limit = 8, onTagClick }: Props = $props();
	
	let popularTags = $state<TagInfo[]>([]);
	let loading = $state(true);
	
	onMount(async () => {
		try {
			popularTags = await tagManager.getPopularTags(locale, limit);
		} catch (error) {
			console.error('Error loading popular tags:', error);
		} finally {
			loading = false;
		}
	});
	
	function handleTagClick(tag: string) {
		if (onTagClick) {
			onTagClick(tag);
		} else {
			// Navigate to tag page
			window.location.href = `/docs/tags/${encodeURIComponent(tag)}`;
		}
	}
</script>

<div class="popular-tags">
	<h3 class="popular-tags__title">{$_('docs.popularTags.title')}</h3>
	
	{#if loading}
		<div class="popular-tags__loading">
			<div class="popular-tags__skeleton"></div>
			<div class="popular-tags__skeleton"></div>
			<div class="popular-tags__skeleton"></div>
			<div class="popular-tags__skeleton"></div>
		</div>
	{:else if popularTags.length > 0}
		<div class="popular-tags__list">
			{#each popularTags as tag}
				<button
					class="popular-tag"
					onclick={() => handleTagClick(tag.name)}
					title={$_(`docs.popularTags.articleCount${tag.count === 1 ? '_one' : '_other'}`, { values: { count: tag.count } })}
				>
					<span class="popular-tag__name">#{tag.name}</span>
					<span class="popular-tag__count">{tag.count}</span>
				</button>
			{/each}
		</div>
		
		<a href="/docs/tags" class="popular-tags__view-all">
			{$_('docs.popularTags.viewAll')}
			<svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
				<path d="M4.5 2.25L7.5 6L4.5 9.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
			</svg>
		</a>
	{:else}
		<p class="popular-tags__empty">{$_('docs.popularTags.empty')}</p>
	{/if}
</div>

<style>
	.popular-tags {
		padding: 1.25rem;
		background: var(--bg-secondary);
		border-radius: 8px;
		border: 1px solid var(--border-light);
	}
	
	.popular-tags__title {
		font-size: 0.75rem;
		font-weight: 600;
		color: var(--text-tertiary);
		text-transform: uppercase;
		letter-spacing: 0.1em;
		margin: 0 0 1rem 0;
	}
	
	.popular-tags__loading {
		display: flex;
		flex-wrap: wrap;
		gap: 0.5rem;
	}
	
	.popular-tags__skeleton {
		height: 28px;
		width: 80px;
		background: var(--bg-tertiary);
		border-radius: 14px;
		animation: pulse 1.5s ease-in-out infinite;
	}
	
	@keyframes pulse {
		0%, 100% {
			opacity: 1;
		}
		50% {
			opacity: 0.5;
		}
	}
	
	.popular-tags__list {
		display: flex;
		flex-wrap: wrap;
		gap: 0.5rem;
		margin-bottom: 1rem;
	}
	
	.popular-tag {
		display: inline-flex;
		align-items: center;
		gap: 0.375rem;
		padding: 0.375rem 0.75rem;
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: 14px;
		font-size: 0.75rem;
		color: var(--text-secondary);
		cursor: pointer;
		transition: all 0.2s ease;
	}
	
	.popular-tag:hover {
		background: var(--bg-tertiary);
		border-color: var(--brand-primary);
		color: var(--brand-primary);
		transform: translateY(-1px);
	}
	
	.popular-tag__name {
		font-weight: 500;
	}
	
	.popular-tag__count {
		padding: 0.125rem 0.375rem;
		background: var(--bg-secondary);
		border-radius: 10px;
		font-size: 0.75rem;
		color: var(--text-tertiary);
		font-weight: 600;
	}
	
	.popular-tag:hover .popular-tag__count {
		background: var(--brand-muted);
		color: var(--brand-primary);
	}
	
	.popular-tags__view-all {
		display: inline-flex;
		align-items: center;
		gap: 0.25rem;
		font-size: 0.75rem;
		color: var(--text-tertiary);
		text-decoration: none;
		transition: color 0.2s ease;
	}
	
	.popular-tags__view-all:hover {
		color: var(--brand-primary);
	}
	
	.popular-tags__view-all svg {
		transition: transform 0.2s ease;
	}
	
	.popular-tags__view-all:hover svg {
		transform: translateX(2px);
	}
	
	.popular-tags__empty {
		font-size: 0.75rem;
		color: var(--text-tertiary);
		margin: 0;
	}
	
	/* Dark mode */
	:global([data-theme='dark']) .popular-tags {
		background: rgba(255, 255, 255, 0.02);
		border-color: rgba(255, 255, 255, 0.08);
	}
	
	:global([data-theme='dark']) .popular-tag {
		background: rgba(255, 255, 255, 0.04);
		border-color: rgba(255, 255, 255, 0.08);
	}
	
	:global([data-theme='dark']) .popular-tag:hover {
		background: rgba(255, 94, 126, 0.1);
		border-color: #ff5e7e;
		color: #ff7a99;
	}
	
	:global([data-theme='dark']) .popular-tag__count {
		background: rgba(255, 255, 255, 0.06);
	}
	
	:global([data-theme='dark']) .popular-tag:hover .popular-tag__count {
		background: rgba(255, 94, 126, 0.15);
		color: #ff7a99;
	}
</style>